import { type Ref, useEffect, useRef, useState } from "react";

import {
  ArrayIcon,
  Button,
  Column,
  EmptyState,
  IconButton,
  RefreshIcon,
  Tooltip,
} from "@hightouch/htui";
import { matchSorter } from "match-sorter";

import { useDestinationForm } from "src/contexts/destination-form-context";
import {
  type ColumnOption,
  useFormkitContext,
} from "src/formkit/components/formkit-context";
import { useMapperListbox } from "src/formkit/components/mapper-v2/hooks/use-mapper-listbox";
import {
  MapperList,
  MapperListRow,
  MapperListSkeleton,
  MapperRowIcon,
  MapperSearch,
} from "src/formkit/components/mapper-v2/mapper-list";
import {
  getValueType,
  type MapperValue,
} from "src/formkit/components/mapper-v2/mapper-selection";
import {
  type JsonColumnProps,
  MappingType,
} from "src/formkit/components/types";
import { extractColumnValues } from "src/formkit/components/utils";
import { usePartner } from "src/partner/context";
import { Link } from "src/router";

type ArrayPaneProps = {
  /** Event models offer the array properties from this list. */
  columnOptions: ColumnOption[] | undefined;
  /** The committed mapping value, whose column is shown as chosen. */
  value: MapperValue;
  /** Each model column's element properties, keyed by column. */
  allColumnsProps: JsonColumnProps["allColumnsProps"];
  searchRef: Ref<HTMLInputElement>;
  onChoose: (value: MapperValue) => void;
  onClose: () => void;
};

type ArrayRow = {
  key: string;
  label: string;
  column: string;
  /** The properties of each element, for model columns. */
  properties?: string;
  isNew: boolean;
  isSelected: boolean;
};

/** Columns that hold an array of objects, to map element by element. */
export function ArrayPane({
  columnOptions,
  value,
  allColumnsProps,
  searchRef,
  onChoose,
  onClose,
}: ArrayPaneProps) {
  const [search, setSearch] = useState("");
  const hasCheckedRowsRef = useRef(false);
  const { reloadRows, loadingRows, rows } = useDestinationForm();
  const { columns: contextColumns, model } = useFormkitContext();
  const { isPartner } = usePartner();
  const isEventModel = Boolean(model?.isEventModel);
  const allColumns = columnOptions ?? contextColumns;
  const committedColumn =
    getValueType(value) === MappingType.ARRAY && typeof value.from === "string"
      ? value.from
      : undefined;

  const hasAnySuggestedArrayColumns =
    !isEventModel ||
    allColumns
      .flatMap((group) => group.options ?? [])
      .some((option) => option?.type === "array");
  const arrayColumns = isEventModel
    ? Array.from(
        extractColumnValues(allColumns, (option) => option?.type === "array"),
      )
    : Object.keys(allColumnsProps ?? {});
  const trimmedSearch = search.trim();
  const matchingColumns = trimmedSearch
    ? matchSorter(arrayColumns, trimmedSearch)
    : arrayColumns;

  // An expected array column that the event schema doesn't list keeps a chosen row of its own.
  const unlistedCommittedColumn =
    isEventModel && committedColumn && !arrayColumns.includes(committedColumn)
      ? committedColumn
      : undefined;
  // Column names are case-sensitive as they match event property names.
  const canCreateColumn =
    isEventModel &&
    Boolean(trimmedSearch) &&
    !matchingColumns.includes(trimmedSearch) &&
    trimmedSearch !== unlistedCommittedColumn;

  const arrayRows: ArrayRow[] = [
    ...(canCreateColumn
      ? [
          {
            key: `create:${trimmedSearch}`,
            label: `Use expected array column name "${trimmedSearch}"…`,
            column: trimmedSearch,
            isNew: true,
            isSelected: false,
          },
        ]
      : []),
    ...matchingColumns.map((column) => ({
      key: column,
      label: column,
      column,
      properties: isEventModel
        ? undefined
        : Array.from(allColumnsProps?.[column] ?? []).join(", "),
      isNew: false,
      isSelected: column === committedColumn,
    })),
    ...(unlistedCommittedColumn
      ? [
          {
            key: `committed:${unlistedCommittedColumn}`,
            label: `Use expected array column name "${unlistedCommittedColumn}"…`,
            column: unlistedCommittedColumn,
            isNew: true,
            isSelected: true,
          },
        ]
      : []),
  ];

  const chooseRow = (row: ArrayRow) => {
    if (row.isSelected) {
      onClose();
      return;
    }
    onChoose({ type: MappingType.ARRAY, from: row.column });
  };

  const listbox = useMapperListbox({
    optionCount: loadingRows && !isEventModel ? 0 : arrayRows.length,
    hasSearch: Boolean(trimmedSearch),
    onChoose: (index) => {
      const row = arrayRows[index];
      if (row) chooseRow(row);
    },
  });

  // A model column's element properties come from preview rows, so the pane runs a preview when there are none.
  // Only the rows at mount decide, so a preview that returns no rows isn't requested again.
  useEffect(() => {
    if (hasCheckedRowsRef.current) return;
    hasCheckedRowsRef.current = true;
    if (!isEventModel && !rows?.length) reloadRows();
  }, [isEventModel, rows, reloadRows]);

  const refreshLabel = isEventModel
    ? "Refresh schema"
    : "Refresh array properties";
  const searchLabel =
    isEventModel && !hasAnySuggestedArrayColumns
      ? "Enter an expected array column"
      : "Search array columns";

  const renderList = () => {
    if (loadingRows && !isEventModel) {
      return <MapperListSkeleton />;
    }
    if (arrayRows.length > 0) {
      return (
        <MapperList
          activeOptionId={listbox.activeOptionId}
          label="Array columns"
          listId={listbox.listId}
          scrollRef={listbox.scrollRef}
        >
          {arrayRows.map((row, index) => (
            <MapperListRow
              key={row.key}
              description={row.properties || undefined}
              hasDescriptionBelow
              icon={row.isNew ? undefined : <MapperRowIcon icon={ArrayIcon} />}
              id={listbox.getOptionId(index)}
              isActive={index === listbox.activeIndex}
              isSelected={row.isSelected}
              label={row.label}
              onChoose={() => chooseRow(row)}
            />
          ))}
        </MapperList>
      );
    }
    return (
      <Column flex={1} justifyContent="center" p={6}>
        {isEventModel ? (
          <EmptyState
            message="Enter the name of an expected array column to map it."
            title="No array columns yet"
            variant="minimal"
          />
        ) : trimmedSearch ? (
          <EmptyState
            title="No array columns match your search"
            variant="minimal"
          />
        ) : (
          <EmptyState
            actions={
              <Button variant="primary" onClick={reloadRows}>
                Reload columns
              </Button>
            }
            // Partner portals embed Hightouch, so they don't link to its docs.
            message={
              isPartner ? undefined : (
                <>
                  Reference our{" "}
                  <Link
                    href={`${import.meta.env.VITE_DOCS_URL}/syncs/mapping-data#required-data-format`}
                    isExternal
                  >
                    documentation
                  </Link>{" "}
                  to find out about the required data format to use the array
                  in-line mapper.
                </>
              )
            }
            title="No eligible columns found"
            variant="minimal"
          />
        )}
      </Column>
    );
  };

  return (
    <>
      <MapperSearch
        actions={
          <Tooltip message={refreshLabel}>
            <IconButton
              aria-label={refreshLabel}
              icon={RefreshIcon}
              isLoading={loadingRows}
              size="md"
              variant="secondary"
              onClick={reloadRows}
            />
          </Tooltip>
        }
        inputRef={searchRef}
        label={searchLabel}
        listbox={listbox}
        placeholder={`${searchLabel}…`}
        value={search}
        onChange={(nextSearch) => {
          setSearch(nextSearch);
          listbox.resetActiveIndex();
        }}
      />
      {renderList()}
    </>
  );
}
